import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { simulacoes } from '@modules/financial-planning/constants/URL'
class APIFinancialPlanningSimulacoes extends APITemplate {
  constructor() {
    super()

    this.reqCarregar = {}
  }

  _shouldSkipLoad(clientId, forceUpdate) {
    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    const storedClientId = store.getters['financialPlanning/simulacoesClientId']
    const sameClient = String(storedClientId) === String(clientId)
    const storedLastUpdate =
      store.getters['financialPlanning/simulacoesLastUpdate']

    const cachedValue = {
      status: 200,
      value: store.getters['financialPlanning/simulacoes'] || [],
    }

    if (!forceUpdate && sameClient && storedLastUpdate) {
      const shouldUpdate = diffTimeMinutes(
        storedLastUpdate,
        MINUTES_TIMEOUT_REQUESTS
      )
      if (!shouldUpdate) return cachedValue
    }

    if (store.getters['financialPlanning/simulacoesLoading'] && sameClient) {
      return cachedValue
    }

    return null
  }

  _handleResponse(response, clientId) {
    if (response && response.status === 200) {
      const items = Array.isArray(response.value) ? response.value : []
      store.dispatch('financialPlanning/setSimulacoes', { items, clientId })
    } else {
      store.dispatch('financialPlanning/setSimulacoes', {
        items: [],
        clientId: null,
      })
    }
  }

  async _executeCarregar(clientId, requestKey) {
    store.dispatch('financialPlanning/setSimulacoesLoading', true)
    this.reqCarregar[requestKey] = con.get(simulacoes.list(clientId))

    try {
      const response = await this.reqCarregar[requestKey]
      this._handleResponse(response, clientId)
      return response
    } catch (error) {
      this._handleResponse(null, clientId)
      throw error
    } finally {
      store.dispatch('financialPlanning/setSimulacoesLoading', false)
      delete this.reqCarregar[requestKey]
    }
  }

  async carregar({ clientId, forceUpdate = false } = {}) {
    const skipResult = this._shouldSkipLoad(clientId, forceUpdate)
    if (skipResult) return skipResult

    const requestKey = `${clientId}`

    if (this.reqCarregar[requestKey]) {
      return this.reqCarregar[requestKey]
    }

    const response = await this._executeCarregar(clientId, requestKey)

    return response || {}
  }

  async simularMonteCarlo({ clientId, simulacao } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!simulacao || typeof simulacao !== 'object') {
      return {
        status: 400,
        message: 'Dados da simulação inválidos.',
      }
    }

    const payload = {
      parameters: JSON.stringify(simulacao),
    }

    const response = await con.post(simulacoes.monteCarlo(clientId), payload)

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

  // Prévia (AAWZ-10): roda o motor no BACKEND sem persistir nada. Diferente de
  // `simularMonteCarlo`/`atualizarMonteCarlo`, NUNCA recarrega a lista
  // (`carregar`) nem despacha ao store — o resultado é efêmero, usado só para
  // ler `resultado_probabilidade_sucesso` (fonte de verdade), e o caller
  // decide onde guardar (estado local do laboratório/bloco CRM).
  async simularPreview({ clientId, simulacao } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!simulacao || typeof simulacao !== 'object') {
      return {
        status: 400,
        message: 'Dados da simulação inválidos.',
      }
    }

    const payload = {
      parameters: JSON.stringify(simulacao),
    }

    const response = await con.post(simulacoes.monteCarloPreview(clientId), payload)

    return response || {}
  }

  async atualizarMonteCarlo({ clientId, simulacaoId, simulacao } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!simulacao || typeof simulacao !== 'object') {
      return {
        status: 400,
        message: 'Dados da simulação inválidos.',
      }
    }

    const simulationId = simulacaoId || simulacao.id
    if (!simulationId) {
      return {
        status: 404,
        message: 'Simulação não encontrada.',
      }
    }

    const payload = {
      parameters: JSON.stringify(simulacao),
    }

    const response = await con.put(
      simulacoes.monteCarloDetail(clientId, simulationId),
      payload
    )

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

  async atualizarSimulacaoPrincipal({ clientId, simulacao } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!simulacao || typeof simulacao !== 'object') {
      return {
        status: 400,
        message: 'Dados da simulação inválidos.',
      }
    }

    const simulationId = simulacao.id
    if (!simulationId) {
      return {
        status: 400,
        message: 'Simulação não informada.',
      }
    }

    const payload = {
      data: JSON.stringify({
        nome_simulacao: simulacao.nome_simulacao,
        descricao: simulacao.descricao,
        principal: true,
      }),
    }

    const response = await con.put(
      simulacoes.detail(clientId, simulationId),
      payload
    )

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

  async remover({ clientId, simulacaoId } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!simulacaoId) {
      return {
        status: 404,
        message: 'Simulação não encontrada.',
      }
    }

    store.dispatch('financialPlanning/setSimulacoesLoading', true)

    const response = await con.delet(
      simulacoes.detail(clientId, simulacaoId)
    )

    store.dispatch('financialPlanning/setSimulacoesLoading', false)

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

  async carregarMonteCarlo({ clientId, idSimulacao } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    const response = await con.get(
      simulacoes.getMonteCarloDetail(clientId, idSimulacao)
    )

    return response || {}
  }
}

const apiFinancialPlanningSimulacoes = new APIFinancialPlanningSimulacoes()
export { apiFinancialPlanningSimulacoes }
